/**
 * @file api.ts
 * @brief TanStack Query hooks for company feature-access toggles.
 *
 * Backs the CompanyAdmin "Features" screen. A company admin reads/patches which
 * products (Interpretation / Notes / Boardroom / Medical Assessment) are
 * enabled for their regular employees. Backend enforces the same flags
 * server-side (requireFeature) and may return platformLocks when Verblizr
 * has killed a product system-wide (09-09-2026).
 *
 * @date 15-07-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { companyEndpoints } from "@/shared/api/endpoints";
import { apiGet, apiPut } from "@/shared/api/client";
import { z } from "zod";

export const FeatureAccessSchema = z.object({
  interpretation: z.boolean(),
  notes: z.boolean(),
  boardroom: z.boolean(),
  medical_assessment: z.boolean(),
  // MCP Phase 2 (06-10-2026). Defaulted so an older backend without the key
  // still parses; off is the safe reading.
  ai_connectors: z.boolean().default(false),
  // ADR-051 (21-10-2026): sending to-dos to Asana, required while "Only
  // share inside the company" is on. Absent on an older backend: no row.
  asana: z.boolean().optional(),
  // Z1 (27-10-2026, ADR-060): Zapier, opt-in, binds admins too. Absent on an
  // older backend: no row.
  zapier: z.boolean().optional(),
  // M1 (29-10-2026): Make, its own opt-in switch, same rules as zapier.
  make: z.boolean().optional(),
});

export type FeatureAccess = z.infer<typeof FeatureAccessSchema>;

export const PlatformLocksSchema = z
  .object({
    interpretation: z.boolean().optional(),
    notes: z.boolean().optional(),
    boardroom: z.boolean().optional(),
    medical_assessment: z.boolean().optional(),
    ai_connectors: z.boolean().optional(),
    asana: z.boolean().optional(),
    zapier: z.boolean().optional(),
    make: z.boolean().optional(),
  })
  .passthrough()
  .default({});

export type PlatformLocks = z.infer<typeof PlatformLocksSchema>;

const CompanyFeaturesResponseSchema = z.object({
  success: z.boolean().optional(),
  features: z.array(z.string()).optional(),
  featureAccess: FeatureAccessSchema,
  platformLocks: PlatformLocksSchema.optional(),
});

export type CompanyFeaturesPayload = {
  featureAccess: FeatureAccess;
  platformLocks: PlatformLocks;
};

export const companyFeaturesKeys = {
  all: ["company", "features"] as const,
};

/** `enabled` lets a page shared with non-admins (Home) skip the admin-only call. */
export function useCompanyFeatures(enabled = true) {
  return useQuery<CompanyFeaturesPayload>({
    queryKey: companyFeaturesKeys.all,
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>(companyEndpoints.features);
      const parsed = CompanyFeaturesResponseSchema.parse(raw);
      return {
        featureAccess: parsed.featureAccess,
        platformLocks: parsed.platformLocks ?? {},
      };
    },
  });
}

export function useUpdateCompanyFeatures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<FeatureAccess>) => {
      const raw = await apiPut<unknown>(companyEndpoints.features, patch);
      const parsed = CompanyFeaturesResponseSchema.parse(raw);
      return {
        featureAccess: parsed.featureAccess,
        platformLocks: parsed.platformLocks ?? {},
      };
    },
    onSuccess: (data) => {
      qc.setQueryData(companyFeaturesKeys.all, data);
    },
  });
}
